/*
 * PostHog storefront loader. Included by snippets/posthog.liquid, which passes
 * its configuration as data attributes on the <script> tag:
 *   data-key       PostHog project key (phc_...)
 *   data-host      API host (EU or US cloud)
 *   data-customer  Shopify customer id, empty for guests
 *
 * This lives in an asset instead of inline in the snippet because Theme
 * Check's Liquid-HTML parser cannot parse PostHog's minified loader stub inside
 * a <script> tag (LiquidHTMLSyntaxError), even inside {% raw %}.
 *
 * CONSENT: PostHog is not even loaded until analytics consent is given. Two
 * signals must both agree:
 *   - Cookiebot, the cookie banner visitors actually answer: it must have a
 *     response with the statistics category accepted.
 *   - Shopify's Customer Privacy API: analyticsProcessingAllowed().
 * The Shopify API alone is not enough. It reports true while the visitor has
 * not answered Cookiebot yet, because Cookiebot only passes a choice to Shopify
 * once there is one; on 30 September 2026 that loaded PostHog (array.js, the
 * recorder and surveys) under an unanswered banner. Without Cookiebot on the
 * page, or when the Shopify API cannot be loaded, PostHog stays unloaded
 * (fails closed). Once loaded it starts opted out and follows every later
 * choice from either side, in both directions.
 *
 * Cart and checkout events are NOT sent from here; they come from the custom
 * web pixel in docs/posthog-web-pixel.js. Adding them here as well counts every
 * cart add twice.
 */
(function () {
  var script = document.currentScript;
  var key = script && script.dataset.key;
  if (!key) return;
  var host = script.dataset.host || 'https://eu.i.posthog.com';
  // Customer id only, never the email: PostHog does not need PII.
  var customerId = script.dataset.customer || '';

  // Official PostHog loader stub (posthog.com/docs/libraries/js), unchanged.
  !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],Object.defineProperty(u,"toString",{configurable:!0,enumerable:!0,writable:!0,value:function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e}}),Object.defineProperty(u.people,"toString",{configurable:!0,enumerable:!0,writable:!0,value:function(){return u.toString(1)+".people (stub)"}}),o="init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagResult isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);

  function applyConsent(ph, allowed) {
    if (allowed) {
      // No `$opt_in` event: it arrived once per session (~9% of all events)
      // and nothing reads it, while it counts against the PostHog event quota.
      if (!ph.has_opted_in_capturing()) {
        ph.opt_in_capturing({ captureEventName: false });
        // Consent given after the flags loaded: the onFeatureFlags callback in `loaded`
        // skipped them, so fetch them again to store them for the next page.
        ph.reloadFeatureFlags();
      }
      if (customerId) ph.identify(customerId);
    } else if (!ph.has_opted_out_capturing()) {
      ph.opt_out_capturing();
      // Without consent the theme settings decide again.
      if (window.gbExperiments) window.gbExperiments.clear();
    }
  }

  function shopifyAllows() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    return !!(privacy && typeof privacy.analyticsProcessingAllowed === 'function' &&
      privacy.analyticsProcessingAllowed());
  }

  function cookiebotAllows() {
    var cookiebot = window.Cookiebot;
    return !!(cookiebot && cookiebot.hasResponse && cookiebot.consent && cookiebot.consent.statistics);
  }

  function analyticsAllowed() {
    return shopifyAllows() && cookiebotAllows();
  }

  var started = false;
  var instance = null;
  function start() {
    if (started) return;
    started = true;

    window.posthog.init(key, {
      api_host: host,
      defaults: '2026-05-30',
      person_profiles: 'identified_only',
      opt_out_capturing_by_default: true,
      // Stamps the producer on every event. before_send is part of the init
      // config, so it also covers events captured before `loaded` runs and
      // replay $snapshot envelopes, which register() does not reach. A
      // caller-supplied `app` is overwritten, so it cannot spoof the producer.
      before_send: function (event) {
        if (!event) return event;
        event.properties = event.properties || {};
        event.properties.app = 'green-bubble';
        return event;
      },
      loaded: function (ph) {
        // A customer logged out, but the cookie still carries their identity.
        // Without this the next visitor on a shared device, including their
        // checkout (the web pixel reads the same cookie), lands on that customer.
        if (!customerId && ph.get_property('$user_state') === 'identified') ph.reset();

        instance = ph;
        applyConsent(ph, analyticsAllowed());

        // Feature flags of the mobile improvements (snippets/gb-experiments.liquid). The
        // theme settings decide; only a running experiment's "control" or "test" variant
        // overrides them per visitor. Fresh flags are not applied to this page: the page
        // would switch version while the visitor reads it. PostHog stores them, and the
        // flag script applies them from the next page on. The $feature_flag_called
        // exposure event that experiments count is only sent once the page shows the
        // visitor's variant.
        ph.onFeatureFlags(function () {
          var experiments = window.gbExperiments;
          if (!experiments || !ph.has_opted_in_capturing()) return;
          for (var flag in experiments.flags) {
            if (!Object.prototype.hasOwnProperty.call(experiments.flags, flag)) continue;
            if (experiments.shows(flag, ph.getFeatureFlag(flag, { send_event: false }))) {
              ph.getFeatureFlag(flag);
            }
          }
        });
      },
    });
  }

  // Every consent signal, from either side, goes through here: before PostHog
  // is loaded it may start it, afterwards it applies the choice in both
  // directions.
  function onConsentChange() {
    if (instance) {
      applyConsent(instance, analyticsAllowed());
    } else if (analyticsAllowed()) {
      start();
    }
  }

  document.addEventListener('visitorConsentCollected', onConsentChange);
  window.addEventListener('CookiebotOnConsentReady', onConsentChange);
  window.addEventListener('CookiebotOnAccept', onConsentChange);
  window.addEventListener('CookiebotOnDecline', onConsentChange);

  if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
  window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
    if (error) return; // stays unloaded
    onConsentChange();
  });
})();
